/**
 * EmployeesTab Component
 * Manages team members/employees with CRUD operations and permissions
 */
import { useState } from 'react';
import { UserPlus, Pencil, Trash2, Settings, AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { employeesAPI } from '@/lib/api';

export const EmployeesTab = ({ 
  employees, 
  onRefresh, 
  onOpenPermissions 
}) => {
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deletingEmployee, setDeletingEmployee] = useState(null);
  const [reactivating, setReactivating] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    department: '',
    position: ''
  });

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await employeesAPI.create(newEmployee);
      toast.success('Employee added successfully');
      setShowAddEmployee(false);
      setNewEmployee({ full_name: '', email: '', password: '', phone: '', department: '', position: '' });
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    }
  };

  const openEditEmployee = (emp) => {
    setEditingEmployee({ ...emp, newPassword: '' });
    setShowEditEmployee(true);
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    if (!editingEmployee) return;
    try {
      const updateData = {
        full_name: editingEmployee.full_name,
        email: editingEmployee.email,
        phone: editingEmployee.phone,
        department: editingEmployee.department,
        position: editingEmployee.position
      };
      if (editingEmployee.newPassword) {
        updateData.password = editingEmployee.newPassword;
      }
      await employeesAPI.update(editingEmployee.id, updateData);
      toast.success('Employee updated successfully');
      setShowEditEmployee(false);
      setEditingEmployee(null);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    }
  };

  const openDeleteConfirm = (emp) => {
    setDeletingEmployee(emp);
    setShowDeleteConfirm(true);
  };

  const handleDeleteEmployee = async () => {
    if (!deletingEmployee) return;
    try {
      await employeesAPI.delete(deletingEmployee.id);
      toast.success('Employee deleted successfully');
      setShowDeleteConfirm(false);
      setDeletingEmployee(null);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const handleReactivate = async (emp) => {
    setReactivating(emp.id);
    try {
      const res = await employeesAPI.reactivate(emp.id);
      toast.success(res.data.message);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setReactivating(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Team Members</h3>
        <Dialog open={showAddEmployee} onOpenChange={setShowAddEmployee}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 text-white hover:bg-teal-700 font-semibold" data-testid="add-employee-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#f1f3f5] border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-gray-800 dark:text-white">Add New Employee</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddEmployee} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Full Name</Label>
                <Input
                  value={newEmployee.full_name}
                  onChange={(e) => setNewEmployee({ ...newEmployee, full_name: e.target.value })}
                  className="bg-[#e9ecef] border-gray-200"
                  required
                  data-testid="emp-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Email</Label>
                <Input
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                  className="bg-[#e9ecef] border-gray-200"
                  required
                  data-testid="emp-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Password</Label>
                <Input
                  type="password"
                  value={newEmployee.password}
                  onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                  className="bg-[#e9ecef] border-gray-200"
                  required
                  data-testid="emp-password"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-600 dark:text-gray-300">Phone</Label>
                  <Input
                    value={newEmployee.phone}
                    onChange={(e) => setNewEmployee({ ...newEmployee, phone: e.target.value })}
                    className="bg-[#e9ecef] border-gray-200"
                    required
                    data-testid="emp-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600 dark:text-gray-300">Department</Label>
                  <Input
                    value={newEmployee.department}
                    onChange={(e) => setNewEmployee({ ...newEmployee, department: e.target.value })}
                    className="bg-[#e9ecef] border-gray-200"
                    required
                    data-testid="emp-department"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Position</Label>
                <Input
                  value={newEmployee.position}
                  onChange={(e) => setNewEmployee({ ...newEmployee, position: e.target.value })}
                  className="bg-[#e9ecef] border-gray-200"
                  required
                  data-testid="emp-position"
                />
              </div>
              <Button type="submit" className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold" data-testid="emp-submit">
                Add Employee
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {employees.map((emp) => (
          <div key={emp.id} className={`bg-[#e9ecef]/50 dark:bg-slate-700/50 rounded-xl p-5 ${emp.is_active === false ? 'opacity-70 border-2 border-red-200' : ''}`} data-testid={`admin-emp-${emp.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${emp.is_active === false ? 'bg-red-100' : 'bg-teal-600/20'}`}>
                  <span className={`text-lg font-semibold ${emp.is_active === false ? 'text-red-500' : 'text-teal-600'}`}>
                    {emp.full_name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="text-gray-800 dark:text-white font-medium flex items-center gap-2">
                    {emp.full_name}
                    {emp.is_active === false && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full" data-testid={`emp-deactivated-badge-${emp.id}`}>Deactivated</span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{emp.position}</p>
                  {emp.transferred_to_name && (
                    <p className="text-xs text-gray-400 mt-0.5">Transferred to: {emp.transferred_to_name}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => openEditEmployee(emp)}
                  className="h-8 w-8 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20"
                  data-testid={`emp-edit-${emp.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => openDeleteConfirm(emp)}
                  className="h-8 w-8 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  data-testid={`emp-delete-${emp.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2 text-sm mb-3">
              <p className="text-gray-500 dark:text-gray-400">{emp.email}</p>
              <p className="text-gray-500 dark:text-gray-400">{emp.department}</p>
            </div>
            <Button
              size="sm"
              onClick={() => onOpenPermissions?.(emp)}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-slate-600 dark:hover:bg-slate-500 dark:text-white"
              data-testid={`emp-permissions-${emp.id}`}
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Permissions
            </Button>
            
            {emp.is_active === false && (
              <Button
                size="sm"
                onClick={() => handleReactivate(emp)}
                disabled={reactivating === emp.id}
                className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white font-semibold"
                data-testid={`emp-reactivate-${emp.id}`}
              >
                <RotateCcw className={`w-4 h-4 mr-2 ${reactivating === emp.id ? 'animate-spin' : ''}`} />
                {reactivating === emp.id ? 'Reactivating...' : 'Reactivate Account'}
              </Button>
            )}
            
            {/* Permission Summary */}
            {emp.permissions && (
              <div className="mt-2 flex flex-wrap gap-1">
                {emp.permissions.leads_view && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Leads</span>
                )}
                {emp.permissions.students_view && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Students</span>
                )}
                {emp.permissions.applications_view && (
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full">Apps</span>
                )}
                {emp.permissions.agents_view && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Agents</span>
                )}
                {emp.permissions.payouts_view && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Payouts</span>
                )}
                {emp.permissions.tasks_assign && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Tasks</span>
                )}
                {!emp.permissions.leads_view && !emp.permissions.students_view && !emp.permissions.applications_view && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">Basic Access</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit Employee Dialog */}
      <Dialog open={showEditEmployee} onOpenChange={setShowEditEmployee}>
        <DialogContent className="bg-[#f1f3f5] dark:bg-slate-800 border-gray-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Edit Employee</DialogTitle>
          </DialogHeader>
          {editingEmployee && (
            <form onSubmit={handleEditEmployee} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Full Name</Label>
                <Input
                  value={editingEmployee.full_name}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, full_name: e.target.value })}
                  className="bg-[#e9ecef] dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white"
                  required
                  data-testid="edit-emp-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Email</Label>
                <Input
                  type="email"
                  value={editingEmployee.email}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, email: e.target.value })}
                  className="bg-[#e9ecef] dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white"
                  required
                  data-testid="edit-emp-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">New Password (leave blank to keep current)</Label>
                <Input
                  type="password"
                  value={editingEmployee.newPassword || ''}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, newPassword: e.target.value })}
                  className="bg-[#e9ecef] dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white"
                  placeholder="Enter new password..."
                  data-testid="edit-emp-password"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-600 dark:text-gray-300">Phone</Label>
                  <Input
                    value={editingEmployee.phone}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, phone: e.target.value })}
                    className="bg-[#e9ecef] dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white"
                    required
                    data-testid="edit-emp-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600 dark:text-gray-300">Department</Label>
                  <Input
                    value={editingEmployee.department}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, department: e.target.value })}
                    className="bg-[#e9ecef] dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white"
                    required
                    data-testid="edit-emp-department"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-gray-300">Position</Label>
                <Input
                  value={editingEmployee.position}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, position: e.target.value })}
                  className="bg-[#e9ecef] dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white"
                  required
                  data-testid="edit-emp-position"
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowEditEmployee(false)} 
                  className="flex-1 border-gray-300 dark:border-slate-600 dark:text-gray-300"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-teal-600 text-white hover:bg-teal-700 font-semibold" data-testid="edit-emp-submit">
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-[#f1f3f5] dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          {deletingEmployee && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">
                Are you sure you want to delete <strong>{deletingEmployee.full_name}</strong>? 
                This will permanently remove their account and they will no longer be able to log in.
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">
                  <strong>Warning:</strong> This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)} 
                  className="flex-1 border-gray-300 dark:border-slate-600 dark:text-gray-300"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteEmployee} 
                  className="flex-1 bg-red-600 text-white hover:bg-red-700 font-semibold"
                  data-testid="confirm-delete-emp"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Employee
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesTab;
